import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import SlideNavigation from './components/SlideNavigation';
import InvestorRequestForm from './components/InvestorRequestForm';
import TitleSlide from './components/slides/TitleSlide';
import ProblemSlide from './components/slides/ProblemSlide';
import SolutionSlide from './components/slides/SolutionSlide';
import MarketSlide from './components/slides/MarketSlide';
import ProductSlide from './components/slides/ProductSlide';
import BusinessModelSlide from './components/slides/BusinessModelSlide';
import CompetitionSlide from './components/slides/CompetitionSlide';
import TractionSlide from './components/slides/TractionSlide';
import TeamSlide from './components/slides/TeamSlide';
import FinancialsSlide from './components/slides/FinancialsSlide';
import NextStepsSlide from './components/slides/NextStepsSlide';

const PitchDeck = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [investorInfo, setInvestorInfo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pitchDeckRef = useRef(null);
  const navigate = useNavigate();

  const handleAccessGranted = (formData) => {
    setInvestorInfo(formData);
    setHasAccess(true);
  };

  const slides = [
    { component: TitleSlide, title: 'LawDesk KE' },
    { component: ProblemSlide, title: 'The Problem' },
    { component: SolutionSlide, title: 'Our Solution' },
    { component: MarketSlide, title: 'Market Opportunity' },
    { component: ProductSlide, title: 'Product Features' },
    { component: BusinessModelSlide, title: 'Business Model' },
    { component: CompetitionSlide, title: 'Competitive Landscape' },
    { component: TractionSlide, title: 'Development Status' },
    { component: TeamSlide, title: 'Our Team' },
    { component: FinancialsSlide, title: 'Financial Projections' },
    { component: NextStepsSlide, title: 'Next Steps' }
  ];

  const nextSlide = () => {
    if (currentSlide < slides?.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (pitchDeckRef?.current?.requestFullscreen) {
        pitchDeckRef?.current?.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'ArrowRight' || e?.key === ' ') {
      nextSlide();
    } else if (e?.key === 'ArrowLeft') {
      prevSlide();
    } else if (e?.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [currentSlide, hasAccess]);

  // If access not granted, show the request form
  if (!hasAccess) {
    return <InvestorRequestForm onAccessGranted={handleAccessGranted} />;
  }

  const CurrentSlideComponent = slides?.[currentSlide]?.component;

  return (
    <div 
      ref={pitchDeckRef}
      className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 ${
        isFullscreen ? 'fixed inset-0 z-50' : ''
      }`}
    >
      {/* Header */}
      {!isFullscreen && (
        <header className="bg-surface border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-smooth"
              >
                <Icon name="ArrowLeft" size={20} />
                <span>Back to Platform</span>
              </button>
              <div className="h-6 w-px bg-border"></div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Scale" size={16} color="white" />
                </div>
                <div>
                  <h1 className="text-xl font-heading font-bold text-primary">LawDesk KE</h1>
                  <p className="text-sm text-text-secondary">Pitch Deck Presentation</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-text-secondary">
                {currentSlide + 1} of {slides?.length}
              </span>
              <button
                onClick={toggleFullscreen}
                className="p-2 text-text-secondary hover:text-text-primary transition-smooth"
                title="Toggle Fullscreen"
              >
                <Icon name="Maximize" size={20} />
              </button>
            </div>
          </div>
        </header>
      )}
      {/* Main Content */}
      <main className="flex-1">
        {/* Slide Content */}
        <div className={`${isFullscreen ? 'h-screen' : 'min-h-[calc(100vh-80px)]'} flex items-center justify-center p-6`}>
          <div className="w-full max-w-6xl">
            {CurrentSlideComponent && <CurrentSlideComponent />}
          </div>
        </div>

        {/* Navigation */}
        <SlideNavigation
          currentSlide={currentSlide}
          totalSlides={slides?.length}
          slides={slides}
          onNext={nextSlide}
          onPrev={prevSlide}
          onGoTo={goToSlide}
          isFullscreen={isFullscreen}
        />
      </main>
      {/* Fullscreen Exit Button */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="fixed top-4 right-4 p-3 bg-surface/90 backdrop-blur-sm text-text-primary rounded-lg shadow-lg hover:bg-surface transition-smooth z-10"
        >
          <Icon name="X" size={20} />
        </button>
      )}
    </div>
  );
};

export default PitchDeck;